import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type WaitRoomPanelProps = {
  sessionId: string;
  apiBaseUrl: string;
  onError: (message: string) => void;
};

type WaitingParticipant = {
  participantId: string;
  participantName: string;
  joinedAt: number; // timestamp; useful if you later want to sort
};

type BaseNotification = {
  eventType: string;
  sessionId: string;
  participantId: string | undefined;
  participantName: string | undefined;
};

const JOINED_TYPE = "DonorJoinedWaitRoomNotificationDto";
const LEFT_TYPE = "DonorLeftWaitRoomNotificationDto";
const DISMISSED_TYPE = "DonorDismissedFromWaitRoomNotificationDto";
const ADMITTED_TYPE = "DonorAdmittedNotificationDto";
const NO_SUCH_SESSION_TYPE = "NoSuchSessionNotificationDto";
const GENERIC_ERROR_TYPE = "GenericErrorNotificationDto";

export const WaitRoomPanel: React.FC<WaitRoomPanelProps> = ({
  sessionId,
  apiBaseUrl,
  onError
}) => {
  const { t } = useTranslation();
  const [waiting, setWaiting] = useState<WaitingParticipant[]>([]);

  // track which participants are currently being admitted (for button disable/spinner)
  const [admittingIds, setAdmittingIds] = useState<Set<string>>(new Set());

  // track which participants are currently being dismissed (for button disable/spinner)
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());

  const [isMinimized, setIsMinimized] = useState(false);

  const admittedOnceRef = useRef<Set<string>>(new Set());
  const dismissedOnceRef = useRef<Set<string>>(new Set());

  // ---- WebSocket setup ------------------------------------------------------

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    let ws: WebSocket | null = null;

    const wsBase = apiBaseUrl.replace(/^http/, "ws");
    const wsUrl = `${wsBase}/ws/collector/sessions/${sessionId}/wait-room`;

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (cancelled) return;
      };

      ws.onmessage = (event: MessageEvent) => {
        if (cancelled) return;

        let parsed: BaseNotification;
        try {
          parsed = JSON.parse(event.data);
        } catch (e) {
          console.warn("Failed to parse wait-room notification:", e);
          return;
        }

        const { eventType, participantId, participantName } = parsed;

        if (eventType === NO_SUCH_SESSION_TYPE) {
              onError(t("errors.noSuchSession", "No such session"));
              return;
            }

            if (eventType === GENERIC_ERROR_TYPE) {
              onError(t("errors.genericError"));
              return;
            }

        if (!participantId) {
          return;
        }

        if (eventType === JOINED_TYPE) {
          if (!participantName) {
            return;
          }
          setWaiting((prev) => {
            if (prev.some((p) => p.participantId === participantId)) {
              return prev;
            }
            return [
              ...prev,
              { participantId, participantName, joinedAt: Date.now() },
            ];
          });
        } else if (
          eventType === LEFT_TYPE ||
          eventType === ADMITTED_TYPE ||
          eventType === DISMISSED_TYPE
        ) {
          setWaiting((prev) =>
            prev.filter((p) => p.participantId !== participantId)
          );
        }
      };

      ws.onerror = (evt) => {
        console.error("Wait-room WebSocket error", evt);
        if (!cancelled) {
          onError(t('errors.genericError'));
        }
      };

      ws.onclose = () => {
        ws = null;
      };
    } catch (err) {
      console.error("Failed to open wait-room WebSocket", err);
      if (!cancelled) {
        onError(t('errors.genericError'));
      }
    }

    return () => {
      cancelled = true;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, onError, sessionId]);

  // ---- Admit handler --------------------------------------------------------

  const handleAdmit = useCallback(
    async (participantId: string) => {
      if (!sessionId || !participantId) return;

      if (admittedOnceRef.current.has(participantId)) {
        return;
      }
      admittedOnceRef.current.add(participantId);

      setAdmittingIds((prev) => {
        const next = new Set(prev);
        next.add(participantId);
        return next;
      });

      try {
        const url = `${apiBaseUrl}/collector/sessions/${sessionId}/wait-room/admit`;
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ participantId }),
        });

        if (!resp.ok) {
          throw new Error(`Admit failed with status ${resp.status}`);
        }

        // Optimistic removal; server should also send DonorAdmittedWaitRoomNotificationDto
        setWaiting((prev) =>
          prev.filter((p) => p.participantId !== participantId)
        );
      } catch (e) {
        console.error("Failed to admit participant", e);
        onError(t('errors.genericError'));
      } finally {
        setAdmittingIds((prev) => {
          const next = new Set(prev);
          next.delete(participantId);
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBaseUrl, onError, sessionId]
  );

  const handleDismiss = useCallback(
    async (participantId: string) => {
      if (!sessionId || !participantId) return;

      if (dismissedOnceRef.current.has(participantId)) {
        return;
      }
      dismissedOnceRef.current.add(participantId);

      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.add(participantId);
        return next;
      });

      try {
        const url = `${apiBaseUrl}/collector/sessions/${sessionId}/wait-room/dismiss`;
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ participantId }),
        });

        if (!resp.ok) {
          throw new Error(`Dismiss failed with status ${resp.status}`);
        }
        setWaiting((prev) =>
          prev.filter((p) => p.participantId !== participantId)
        );
      } catch (e) {
        console.error("Failed to dismiss participant", e);
        onError(t("errors.genericError"));
      } finally {
        setDismissingIds((prev) => {
          const next = new Set(prev);
          next.delete(participantId);
          return next;
        });
      }
    },
    [apiBaseUrl, sessionId]
  );

  if (waiting.length === 0) {
    return null;
  }

  const waitingCount = waiting.length;

  if (isMinimized) {
    return (
      <div className="fixed bottom-5 right-5 z-40 bg-black/70 rounded">
        <button
          type="button"
          onClick={() => setIsMinimized(false)}
          className="card wait-room-host-panel px-3 py-2 flex items-center gap-2 shadow-lg"
        >
          <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          <span className="text-sm text-white">
            {waitingCount === 1
              ? t("waitRoom.minimizedSingle", "1 participant in the wait room")
              : t(
                  "waitRoom.minimizedMultiple",
                  "{{count}} participants in the wait room",
                  { count: waitingCount }
                )}
          </span>
        </button>
      </div>
    );
  }

  // ---- Overlay UI -------------------------------------------------------------------

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Panel */}
      <div className="relative z-50 w-full max-w-md px-4 bg-gray-900/85 rounded">
        <section className="card wait-room-host-panel px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white">
                {t("waitRoom.title", "Wait room")}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {/* Minimize button */}
              <button
                type="button"
                onClick={() => setIsMinimized(true)}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white text-sm font-bold"
                title={t("waitRoom.minimize", "Minimize")}
              >
                <span className="leading-none -mt-[1px]">−</span>
              </button>
            </div>
          </div>

          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {waiting.map((p) => {
              const isAdmitting = admittingIds.has(p.participantId);
              const isDismissing = dismissingIds.has(p.participantId);
              return (
                <li
                  key={p.participantId}
                  className="flex flex-col gap-1 rounded bg-black/50 px-3 py-2"
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-sm text-white font-medium">
                      {p.participantName}
                    </span>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleAdmit(p.participantId)}
                        disabled={isAdmitting}
                        className="ml-3 inline-flex items-center justify-center rounded bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        {isAdmitting
                          ? t("waitRoom.admitting", "Admitting…")
                          : t("waitRoom.admit", "Admit")}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDismiss(p.participantId)}
                        disabled={isDismissing}
                        className="ml-3 inline-flex items-center justify-center rounded bg-red-400/60 px-3 py-1 text-xs font-medium text-white hover:bg-red-400 disabled:opacity-60"
                      >
                        {isDismissing
                          ? t("waitRoom.dismissing", "Dismissing...")
                          : t("waitRoom.dismiss", "Dismiss")}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
};
